/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, Typography2 } from '@coursera/cds-core';

import EnrollStep from 'bundles/degree-description/components/modules/fcnd/EnrollStep';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { appendUtmParametersToApplicationLink } from 'bundles/expression-of-interest/utils/util';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import getDegreesConversionCopy from 'bundles/stackability-common/copy/degreesConversionCopy';

const styles = {
  background: css`
    padding: var(--cds-spacing-600) 0;
  `,
};

type FcndCopy = {
  title: string;
  description: string;
  disclaimer: string;
  ctaLabel: string;
  ctaLink: string | undefined;
  enrollSteps: {
    number: number;
    title: string;
    description: string;
  }[];
};

export const FCNDModule = () => {
  const theme = useDegreeDescriptionPageTheme();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  // @ts-expect-error
  const fcndModuleCopy: FcndCopy | undefined = getDegreesConversionCopy()?.stackabilityModuleRefresh?.fcndModuleCopy;
  const pageSectionTrackingRef = useGetViewPageSectionRef('for_credit_non_degree');

  if (!fcndModuleCopy) {
    return null;
  }

  return (
    <div css={styles.background} ref={pageSectionTrackingRef}>
      <Container>
        <Grid container direction="column" spacing={24}>
          <Grid item container direction="column" spacing={24} sm={12} md={8}>
            <Grid item>
              <Typography2 variant="titleSmall" css={theme.mobileFontSizeStyle?.header} component="h2">
                {fcndModuleCopy.title}
              </Typography2>
            </Grid>

            <Grid item>
              <Typography2 component="p">
                {fcndModuleCopy.description}
                {fcndModuleCopy.ctaLink && (
                  <Link
                    href={appendUtmParametersToApplicationLink(fcndModuleCopy.ctaLink)}
                    target="_blank"
                    variant="quiet"
                    typographyVariant="subtitleMedium"
                    css={{ marginLeft: 'var(--cds-spacing-50)' }}
                    component={TrackedLink2}
                    trackingName="upsells_fcnd"
                  >
                    {fcndModuleCopy.ctaLabel}
                  </Link>
                )}
              </Typography2>
            </Grid>
          </Grid>

          <Grid item container spacing={16}>
            {fcndModuleCopy.enrollSteps.map((step: { number: number; title: string; description: string }) => (
              <EnrollStep key={step.number} {...step} />
            ))}
          </Grid>

          <Grid item>
            <Typography2 component="p">{fcndModuleCopy.disclaimer}</Typography2>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(FCNDModule);
